import { useEffect, useRef } from "react";
import type { EventEntry } from "../../../src/fields.ts";

interface EventMapProps {
  events: EventEntry[];
  country: string;
  center?: [number, number] | null;
  onBoundsChange: (coords: [number, number], radius: number) => void;
}

const countryCenters: Record<string, [number, number]> = {
  Israel: [32.0853, 34.7818],
  US: [40.7128, -74.0060],
  USA: [40.7128, -74.0060],
  Germany: [52.5200, 13.4050],
  Argentina: [-34.6037, -58.3816],
};

// Extract coordinates safely from GeoJSON Point and other formats
const getEventCoordinates = (event: EventEntry): [number, number] | null => {
  const coords = event.structured.coordinates;
  if (!coords) return null;

  if (
    coords.type === "Point" &&
    Array.isArray(coords.coordinates) &&
    coords.coordinates.length >= 2
  ) {
    const [lng, lat] = coords.coordinates;
    if (typeof lat === "number" && typeof lng === "number") {
      return [lat, lng];
    }
  }

  if (Array.isArray(coords) && coords.length >= 2) {
    const [lng, lat] = coords;
    if (typeof lat === "number" && typeof lng === "number") {
      return [lat, lng];
    }
  }

  if (typeof coords === "object") {
    const lat = coords.latitude ?? coords.lat;
    const lng = coords.longitude ?? coords.lng;
    if (typeof lat === "number" && typeof lng === "number") {
      return [lat, lng];
    }
  }

  return null;
};

export const EventMap = (
  { events, country, center, onBoundsChange }: EventMapProps,
) => {
  const mapRef = useRef<HTMLDivElement>(null);
  // deno-lint-ignore no-explicit-any
  const mapInstance = useRef<any>(null);
  // deno-lint-ignore no-explicit-any
  const markersRef = useRef<any>(null);

  const eventsRef = useRef(events);
  eventsRef.current = events;

  const getInitialCenter = (): [number, number] => {
    // 0. Use the explicitly provided center if any (passed as [lng, lat], convert to [lat, lng] for Leaflet)
    if (center) {
      return [center[1], center[0]];
    }
    // 1. Try to find the first event in the list that has coordinates
    for (const event of eventsRef.current) {
      const latLng = getEventCoordinates(event);
      if (latLng) {
        return latLng;
      }
    }
    // 2. Try the selected country's center
    if (country && countryCenters[country]) {
      return countryCenters[country];
    }
    // 3. Fallback to default (Tel Aviv)
    return [32.0853, 34.7818];
  };

  const updateMarkers = async () => {
    if (!mapInstance.current || !markersRef.current) return;
    const L = (await import("leaflet")).default;

    markersRef.current.clearLayers();

    eventsRef.current.forEach((event) => {
      const latLng = getEventCoordinates(event);
      if (latLng) {
        const [lat, lng] = latLng;
        const marker = L.circleMarker([lat, lng], {
          radius: 8,
          fillColor: "#a855f7",
          color: "#fff",
          weight: 2,
          opacity: 1,
          fillOpacity: 0.8,
        });

        marker.bindPopup(`
          <div style="font-family: system-ui; padding: 4px; color: #1a1a1a;">
            <a href="/event/${event.id}" style="text-decoration: none;">
              <h4 dir="auto" style="font-weight: 700; font-size: 14px; margin: 0; color: #7c3aed; line-height: 1.2; cursor: pointer;">${event.structured.name}</h4>
            </a>
            <p dir="auto" style="font-size: 11px; margin: 4px 0 0 0; color: #6b7280;">${
          event.structured.venue || ""
        }</p>
          </div>
        `);

        markersRef.current!.addLayer(marker);
      }
    });
  };

  useEffect(() => {
    // deno-lint-ignore no-explicit-any
    let map: any = null;

    const initMap = async () => {
      if (!mapRef.current) return;

      // Dynamically import Leaflet only on client side
      const L = (await import("leaflet")).default;

      // Initialize map
      const initialCenter = getInitialCenter();
      map = L.map(mapRef.current, {
        zoomControl: false,
      }).setView(initialCenter, 12);

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "© OpenStreetMap",
      }).addTo(map);

      L.control.zoom({ position: "topright" }).addTo(map);

      mapInstance.current = map;
      markersRef.current = L.layerGroup().addTo(map);

      const handleMapChange = () => {
        const center = map.getCenter();
        const bounds = map.getBounds();
        const east = bounds.getEast();

        // Approximate radius in km from map width
        const radiusKm = Math.max(
          1,
          Math.min(
            200,
            Math.round(center.distanceTo([center.lat, east]) / 1000),
          ),
        );

        onBoundsChange([center.lng, center.lat], radiusKm);
      };

      map.on("moveend", handleMapChange);
      map.on("zoomend", handleMapChange);

      // Invalidate layout and trigger bounds calculation after mounting
      setTimeout(() => {
        if (mapInstance.current) {
          mapInstance.current.invalidateSize();
          handleMapChange();
        }
      }, 150);

      // Render any markers immediately on load
      updateMarkers();
    };

    initMap();

    return () => {
      if (map) {
        map.remove();
      }
    };
  }, []);

  useEffect(() => {
    updateMarkers();
  }, [events]);

  return (
    <div className="relative">
      <div
        ref={mapRef}
        className="h-[300px] w-full rounded-2xl overflow-hidden border border-white/10 shadow-lg"
      />
      <div className="absolute bottom-3 left-3 bg-black/60 backdrop-blur-md border border-white/10 rounded-lg px-2.5 py-1.5 text-[10px] text-gray-300 pointer-events-none z-[1000]">
        📍 Drag or zoom map to filter
      </div>
    </div>
  );
};
